import { slugify } from '~/libs/utils/format';

export interface AvailabilitySlugSource {
	atc: string;
	rs: string | null;
}

export type ParsedAutocompleteLabel = AvailabilitySlugSource;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const RS_PREFIX_PATTERN = /^(\d+\/\d+)\s+-\s+/;
const LABEL_SEPARATOR = ' - ';

export function buildGroupSlug({ atc, rs }: AvailabilitySlugSource): string {
	const atcSlug = slugify(atc);
	return rs ? `${atcSlug}-${slugify(rs)}` : atcSlug;
}

export function parseAutocompleteLabel(label: string): ParsedAutocompleteLabel {
	const rsMatch = label.match(RS_PREFIX_PATTERN);
	const rs = rsMatch?.[1] ?? null;
	const withoutRs = rsMatch ? label.slice(rsMatch[0].length) : label;
	const lastSeparatorIndex = withoutRs.lastIndexOf(LABEL_SEPARATOR);
	const atc = lastSeparatorIndex === -1 ? withoutRs : withoutRs.slice(0, lastSeparatorIndex);

	return { atc, rs };
}

export function buildSlugFromLabel(label: string): string {
	return buildGroupSlug(parseAutocompleteLabel(label));
}

export function extractAtcCode(slug: string): string {
	return slug.split('-')[0] ?? '';
}

export function isUuid(token: string): boolean {
	return UUID_PATTERN.test(token);
}

const RS_SLUG_SUFFIX_PATTERN = /-\d+-\d+$/;

export function matchSlugCandidate<T extends { slug: string }>(requestedSlug: string, candidates: T[]): T | undefined {
	const exact = candidates.find((candidate) => candidate.slug === requestedSlug);
	if (exact) {
		return exact;
	}

	const requestedCode = extractAtcCode(requestedSlug);
	const byCode = candidates.filter((candidate) => extractAtcCode(candidate.slug) === requestedCode);
	if (byCode.length === 0) {
		return undefined;
	}

	if (byCode.length === 1) {
		return byCode[0];
	}

	if (!RS_SLUG_SUFFIX_PATTERN.test(requestedSlug)) {
		return byCode.find((candidate) => !RS_SLUG_SUFFIX_PATTERN.test(candidate.slug)) ?? byCode[0];
	}

	return byCode[0];
}
